// DOCS → FEATURES — what the project does, derived from the materials.
//
// Versioned, and each version pinned to the MATERIALS version it came from.
// HEURISTIC FIRST (2026-09-03): a materials change files a BUILD card, not a
// review. The analysis is booked from here — "Analyse this project" — and only
// once that build has landed (the backend answers 409 while it is open), so the
// review reads what was BUILT and the features it marks as wanting a task are
// the GAPS — that is the working loop this tab exists to close:
//
//   material changes → feature is marked changed → "+ task" → the task is on
//   the Board → evidence lands on it → the feature reads verified
//
// Each row carries its ORIGIN — for a requested feature, which material item
// and the verbatim quote in it, with a link that opens that place; for an
// as-built one, the APP THAT IMPLEMENTS IT, with a link that opens the app —
// plus its use cases, its status, and its tasks with the evidence on them.
//
// The material origin opens a MODAL over this tab, not a navigation: checking
// "did this really come from there" is something a reader does five times in a
// row while working down the list, and a tab switch costs them their place and
// their scroll position every time. The modal lands on the sentence with the
// quote lit; "Open in Materials" is still there for the reader who wants the
// document itself.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, RefreshCw, ChevronDown, ChevronRight, ExternalLink,
  ClipboardList, AlertTriangle, Check, Pencil, ListChecks, Plus, History, Sparkles,
  LayoutGrid, Trash2, Activity, Tag, GitCompareArrows, FileText, Download, Printer, BookMarked,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import { createAppRequest } from "../../services/apps";
import { createPageUrl } from "@/utils";
import OriginModal from "./OriginModal";
import FeatureTests, { TestsBadge } from "./FeatureTests";
import CompareRequirements from "./CompareRequirements";

// Every kind "+ task" may file (mirrors docs-model TASK_KINDS): the two change
// families plus one BUILD kind per cortex surface. A requirement about an ML
// model, an agent, a business workflow, a guide or a PDF report is not an app
// change, and filing it as one made the fleet hand-roll the capability inside
// a view — the label on the button says which surface the task will reach.
const TASK_KINDS = ["change", "change_pipeline", "new_app", "new_model", "new_agent", "new_workflow", "new_guide", "new_report"];
const TASK_KIND_LABELS = {
  change: "change", change_pipeline: "pipeline change", new_app: "new app", new_model: "new model",
  new_agent: "new agent", new_workflow: "new workflow", new_guide: "new guide", new_report: "new report",
};
// BATCH: several requirements → ONE card, worked in one of two modes (mirrors
// docs-model BATCH_MODES). Heuristic is the default — one session designs and
// builds the whole selection together; algorithmic works it as a checklist.
const BATCH_MODES = [
  ["heuristic", "Heuristic", "one session designs and builds the whole selection together, then verifies and tests each requirement (default)"],
  ["algorithmic", "Algorithmic", "the same session works the requirements one by one — acceptance criteria, implement, verify, evidence, tests — in order"],
];

// Requirement KINDS — the section order of the tab. Internal field stays
// `kind` on the feature row; "feature" remains the storage vocabulary while
// the UI says "requirement" (the storage/strategy → data point/transformation
// pattern).
const KINDS = ["functional", "quality", "constraint", "design", "non_goal", "noise"];
const KIND_META = {
  functional: { section: "What it does", chip: "functional", tone: "bg-card-border/40 text-page-muted" },
  quality: { section: "Qualities — how it must behave", chip: "quality", tone: "bg-sky-500/10 text-sky-700 dark:text-sky-400" },
  constraint: { section: "Constraints", chip: "constraint", tone: "bg-violet-500/10 text-violet-700 dark:text-violet-400" },
  design: { section: "Design & feel", chip: "design", tone: "bg-pink-500/10 text-pink-700 dark:text-pink-400" },
  non_goal: { section: "Out of scope", chip: "out of scope", tone: "bg-card-border/40 text-page-muted" },
  // Text of the sources that asks for NOTHING (headings, boilerplate,
  // background) — recorded so that every passage is linked to a row and
  // nothing in the materials is ignored. Takes no tasks, status or tests.
  noise: { section: "Not requirements — text of the sources that asks for nothing", chip: "not a requirement", tone: "bg-card-border/40 text-page-muted" },
};
const kindMeta = (k) => KIND_META[k] || KIND_META.functional;
// The kinds that can never become work (mirrors docs-model NO_TASK_KINDS).
const NO_TASK_KINDS = new Set(["non_goal", "noise"]);
const takesTasks = (k) => !NO_TASK_KINDS.has(k || "functional");

const STATUSES = ["planned", "in_progress", "implemented", "verified"];
const STATUS_META = {
  planned: { label: "Planned", tone: "text-page-muted" },
  in_progress: { label: "In progress", tone: "text-amber-700 dark:text-amber-400" },
  implemented: { label: "Implemented", tone: "text-nav-active" },
  verified: { label: "Verified", tone: "text-emerald-700 dark:text-emerald-400" },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || "—", tone: "text-page-muted" };

const DELTA_META = {
  added: { label: "new", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", why: "This feature is new in the latest review of the materials." },
  changed: { label: "changed", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400", why: "The materials changed what this feature must do." },
  missing: { label: "no longer asked for", tone: "bg-red-500/10 text-red-700 dark:text-red-400", why: "No material supports this feature any more. It is kept for review, not deleted." },
};

// The same Board status vocabulary the requirement tasks use, so a task reads
// identically wherever it is shown.
const boardTone = (st) => st === "done" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
  : st === "running" || st === "claimed" || st === "queued" ? "bg-nav-active/10 text-nav-active"
  : st === "waiting" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
  : st === "failed" || st === "cancelled" ? "bg-red-500/10 text-red-700 dark:text-red-400"
  : "bg-card-border/40 text-page-muted";
const boardLabel = (st) => st === "done" ? "✓ done"
  : st === "running" || st === "claimed" ? "in progress"
  : st === "queued" ? "queued for a worker"
  : st === "waiting" ? "blocked — needs you"
  : st === "todo" ? "in the backlog"
  : st || "on the Board";

export default function FeaturesTab({ projectSlug, jobs, onJobsChange, onOpenMaterial, focusKey, onFocusHandled, compare, onCompareChange }) {
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [wantsOnly, setWantsOnly] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  // PROJECT SUMMARY — the customer-facing account generated from this chain
  // (backend docs-summary.js). The menu offers the print-ready page (the
  // browser's Save as PDF), the Markdown download, saving a copy into the
  // project's Knowledge, and the one human-written part: the notes.
  const [summaryMenu, setSummaryMenu] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState({ audience: "", decisions: "", caveats: "" });
  const [summaryBusy, setSummaryBusy] = useState("");
  // The comparison panel's state lives with the PARENT (URL params), so a
  // link can open the Requirements tab straight on "sources v1 vs v4"; the
  // tab falls back to local state when no parent handles it.
  const [localCompare, setLocalCompare] = useState(null);
  const cmp = compare !== undefined ? compare : localCompare;
  const setCmp = (next) => { if (onCompareChange) onCompareChange(next); else setLocalCompare(next); };
  const [evidence, setEvidence] = useState({});   // taskId -> rows
  // TESTS — every test of the project keyed by requirement, with its newest
  // verdict, plus the fleet jobs currently writing tests. One fetch for the
  // whole tab (a row shows its badge before it is opened); re-polled while a
  // run or a generation is in flight so verdicts land without a reload.
  const [testsByFeature, setTestsByFeature] = useState({});
  const [testJobs, setTestJobs] = useState([]);
  const [testsPoll, setTestsPoll] = useState(0);
  // The origin the reader is looking at, if any. Held as the feature's own
  // origin fields rather than a feature reference, so the modal never has to
  // know what a feature is.
  const [origin, setOrigin] = useState(null);

  const load = useCallback(async (v) => {
    setErr("");
    try {
      const r = await fetchData(`docs/features${v ? `?version=${v}` : ""}`, { method: "GET" });
      setData(r);
    } catch (e) {
      setData({ features: [], tasks: {}, versions: [] });
      setErr(String(e?.message || "Could not read the features."));
    }
  }, []);
  useEffect(() => { setData(null); load(version); }, [projectSlug, version, load]);

  const running = (jobs || []).filter((j) => j.kind === "matrix" && j.status === "running");
  const prevRunning = React.useRef(0);
  useEffect(() => {
    if (prevRunning.current > 0 && running.length < prevRunning.current) load(version);
    prevRunning.current = running.length;
  }, [running.length, load, version]);

  // The gate the backend computed with the features: while the build filed
  // from the current sources is open, the analysis is not offered.
  const analysis = data?.analysis || { allowed: true, state: "none", reason: null };
  const heuristic = data?.heuristic || null;

  const review = async () => {
    setBusy("review"); setMsg("");
    try {
      await fetchData("docs/features/review", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      setMsg("Analysing — the requirements are derived from the sources and checked against what the project already implements; your overrides and tasks are kept.");
      onJobsChange?.();
    } catch (e) { setMsg(String(e?.message || "Could not start the analysis.")); }
    finally { setBusy(""); }
  };

  const openSummaryPage = async () => {
    // Open the tab synchronously (popup blockers) and fill it once the
    // bearer-authenticated fetch returns — a plain link would arrive
    // unauthenticated at the backend.
    const w = window.open("", "_blank");
    setSummaryBusy("page"); setMsg("");
    try {
      const html = await fetchData("docs/summary.html", { responseType: "text" });
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    } catch (e) { if (w) w.close(); setMsg(String(e?.message || "Could not build the summary.")); }
    finally { setSummaryBusy(""); setSummaryMenu(false); }
  };
  const downloadSummaryMd = async () => {
    setSummaryBusy("md"); setMsg("");
    try {
      const md = await fetchData("docs/summary.md", { responseType: "text" });
      const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${projectSlug}-summary-${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) { setMsg(String(e?.message || "Could not build the summary.")); }
    finally { setSummaryBusy(""); setSummaryMenu(false); }
  };
  const archiveSummary = async () => {
    setSummaryBusy("archive"); setMsg("");
    try {
      const r = await fetchData("docs/summary/archive", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      setMsg(`Summary saved to the project's Knowledge as “${r?.title || r?.slug}”.`);
    } catch (e) { setMsg(String(e?.message || "Could not save the summary.")); }
    finally { setSummaryBusy(""); setSummaryMenu(false); }
  };
  const openNotes = async () => {
    setSummaryMenu(false); setSummaryBusy("notes"); setMsg("");
    try {
      const r = await fetchData("docs/summary");
      const n = r?.notes || {};
      setNotes({ audience: n.audience || "", decisions: n.decisions || "", caveats: n.caveats || "" });
      setNotesOpen(true);
    } catch (e) { setMsg(String(e?.message || "Could not load the summary notes.")); }
    finally { setSummaryBusy(""); }
  };
  const saveNotes = async () => {
    setSummaryBusy("save"); setMsg("");
    try {
      await fetchData("docs/summary/notes", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(notes) });
      setNotesOpen(false); setMsg("Summary notes saved — they appear in every download from now on.");
    } catch (e) { setMsg(String(e?.message || "Could not save the notes.")); }
    finally { setSummaryBusy(""); }
  };

  const setOverride = async (slug, status) => {
    setBusy(slug); setMsg("");
    try {
      await fetchData(`docs/features/${encodeURIComponent(slug)}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await load(version);
    } catch (e) { setMsg(String(e?.message || "Could not set the status.")); }
    finally { setBusy(""); }
  };

  // "+ task": propose the task, then file it to the Board — the same two hops
  // the requirement tasks make (create the card, then link the ledger item).
  // `wants` comes from the backend: the request kind the requirement calls for
  // (change to the project's app / pipeline change / new app when there is no
  // app) plus the target app — a requirement is a change to what exists, not a
  // new application each.
  // The kind picker: `kindPick[slug]` overrides the backend's proposal when a
  // person knows better (the text said "model" but they want the screen first).
  const [kindPick, setKindPick] = useState({});
  // BATCH SELECTION: the requirements ticked for ONE common task, and how the
  // fleet should work them. Non-goals cannot be ticked (they take no task).
  const [selected, setSelected] = useState(() => new Set());
  const [batchMode, setBatchMode] = useState("heuristic");
  const toggleSelect = (key) => setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const createBatch = async () => {
    const keys = [...selected];
    if (!keys.length) return;
    setBusy("batch"); setMsg("");
    try {
      const r = await fetchData("docs/features/tasks/batch", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys, mode: batchMode }),
      });
      const n = (r?.tasks || []).length;
      setMsg(`One ${r?.mode || batchMode} task filed as #${r?.request_id} for ${n} requirement${n === 1 ? "" : "s"} — track it on the Board.`);
      setSelected(new Set());
      await load(version);
    } catch (e) {
      setMsg(String(e?.message || "Could not create the task."));
    } finally { setBusy(""); }
  };
  const addTask = async (f, wants) => {
    const kind = kindPick[f.slug] || wants?.kind || "change";
    setBusy(f.slug); setMsg("");
    try {
      const r = await fetchData(`docs/features/${encodeURIComponent(f.slug)}/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind }),
      });
      const t = r?.task;
      if (!t) throw new Error("no task came back");
      const details = [
        t.brief, "",
        `Feature: ${f.name} (${f.slug})`,
        f.quote ? `Origin: "${f.quote}" — ${f.material_name || `material #${f.material_id}`}` : "",
        (f.use_cases || []).length ? `\nUse case:\n${f.use_cases[0]}` : "",
      ].filter(Boolean).join("\n");
      // The requirement's AREA rides onto the card as its group: the fleet
      // claims and builds one group (one screen / pipeline concern) per
      // session, while every requirement keeps its own task.
      const groupKey = t.area || wants?.area || f.area || null;
      const card = await createAppRequest({
        kind: t.kind, title: t.title, details,
        ...(t.kind === "change" && wants?.app_id ? { app_id: wants.app_id } : {}),
        ...(groupKey ? { group_key: groupKey } : {}),
      }, { projectSlug });
      const id = (card?.request || card)?.id;
      if (id) {
        await fetchData(`requirements/${encodeURIComponent(f.slug)}/tasks/${t.id}/link`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ request_id: id }),
        }).catch(() => {});
      }
      setMsg(`Task filed${id ? ` as #${id}` : ""} — track it on the Board.`);
      await load(version);
    } catch (e) {
      setMsg(String(e?.message || "Could not create the task."));
    } finally { setBusy(""); }
  };

  // Remove a requirement row for good. A later review may legitimately re-add
  // it (fresh row) if the sources or the implementation still support it —
  // which is exactly the contract that makes pruning generation noise safe.
  const removeRequirement = async (f) => {
    if (!window.confirm(`Delete the requirement "${f.name}"?\n\nIts tasks stay on the Board. The next review may re-add it if the sources still support it.`)) return;
    setBusy(f.slug); setMsg("");
    try {
      await fetchData(`docs/features/${encodeURIComponent(f.slug)}`, { method: "DELETE" });
      await load(version);
    } catch (e) { setMsg(String(e?.message || "Could not delete the requirement.")); }
    finally { setBusy(""); }
  };

  const loadTests = useCallback(async () => {
    try {
      const [r, j] = await Promise.all([
        fetchData("tests", { method: "GET" }),
        fetchData("tests/generate_status", { method: "GET" }).catch(() => ({ jobs: [] })),
      ]);
      const by = {};
      for (const t of r?.tests || []) (by[t.feature_key || ""] ||= []).push(t);
      setTestsByFeature(by);
      setTestJobs((j?.jobs || []).filter((x) => x.status === "running"));
      return { running: (j?.jobs || []).some((x) => x.status === "running") };
    } catch { return { running: false }; }
  }, []);
  useEffect(() => { loadTests(); }, [loadTests, data?.version]);
  // While something is in flight (a run just started, an agent writing tests),
  // poll every few seconds for up to ~10 minutes.
  useEffect(() => {
    if (!testsPoll) return undefined;
    let ticks = 0;
    const t = setInterval(async () => {
      ticks++;
      const r = await loadTests();
      const anyRunning = r.running || (await fetchData("tests/runs?limit=1", { method: "GET" }).catch(() => null))?.runs?.[0]?.status === "running";
      if (!anyRunning || ticks > 150) { setTestsPoll(0); }
    }, 4000);
    return () => clearInterval(t);
  }, [testsPoll, loadTests]);
  const onTestsChanged = (hint) => { loadTests(); if (hint?.running || hint?.generating) setTestsPoll((n) => n + 1); };

  const loadEvidence = async (taskId) => {
    if (evidence[taskId]) return;
    try {
      const r = await fetchData(`docs/tasks/${encodeURIComponent(taskId)}/evidence`, { method: "GET" });
      setEvidence((m) => ({ ...m, [taskId]: r?.evidence || [] }));
    } catch { setEvidence((m) => ({ ...m, [taskId]: [] })); }
  };

  // "What changed in vN" — the requirements-version axis of the compare panel.
  const toggleDiff = (to) => {
    if (cmp?.by === "features" && cmp.to === to) { setCmp(null); return; }
    setCmp({ by: "features", to, from: null });
  };
  // The sources versions with requirements behind them (distinct pins).
  const sourcesWithRequirements = useMemo(() => {
    const s = new Set();
    for (const v of (data?.versions || [])) if (v.materials_version != null) s.add(v.materials_version);
    return [...s].sort((a, b) => b - a);
  }, [data?.versions]);
  const compareBySources = () => {
    if (cmp?.by === "materials") { setCmp(null); return; }
    setCmp({ by: "materials", to: sourcesWithRequirements[0] ?? null, from: null });
  };

  const toggle = (slug) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(slug)) n.delete(slug);
    else {
      n.add(slug);
      const f = (data?.features || []).find((x) => x.slug === slug);
      for (const id of (f?.tasks || [])) loadEvidence(id);
    }
    return n;
  });

  // Deep link from the Timeline (and from a task's feature link): open that
  // feature's row. The caller addresses a feature by its stable KEY — the slug
  // is a storage detail nothing outside this tab should have to know.
  useEffect(() => {
    if (!focusKey || !data) return;
    const f = (data.features || []).find((x) => x.key === focusKey || x.slug === focusKey);
    if (f) {
      setOpen((o) => new Set(o).add(f.slug));
      for (const id of (f.tasks || [])) loadEvidence(id);
      // Scrolling matters here: the row that was linked to is usually below the
      // fold on a thirty-feature list, and an expanded row nobody can see reads
      // as a link that did nothing.
      requestAnimationFrame(() => {
        document.querySelector(`[data-testid="feature-${CSS.escape(f.key)}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    }
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, data]);

  const features = data?.features || [];
  const tasks = data?.tasks || {};
  const versions = data?.versions || [];
  const cur = data?.current_version || null;
  const viewing = data?.version || null;

  const shown = useMemo(() => features.filter((f) =>
    (statusFilter === "all" || f.status === statusFilter) &&
    (originFilter === "all" || f.origin === originFilter) &&
    (kindFilter === "all" || (f.kind || "functional") === kindFilter) &&
    (!wantsOnly || !!f.wants)), [features, statusFilter, originFilter, kindFilter, wantsOnly]);
  // "Select all" means the rows a task can still deliver: shown, not a non-goal,
  // and NOT implemented / verified (Alex, 2026-09-04) — never the whole list.
  const selectable = useMemo(() => shown.filter((f) => takesTasks(f.kind) && f.status !== "implemented" && f.status !== "verified"), [shown]);

  // The list renders in KIND sections (functional first), each keeping the
  // backend's name order. Sections only appear when the project actually has
  // more than one kind — a purely functional list stays a flat list.
  const sections = useMemo(() => {
    const by = new Map(KINDS.map((k) => [k, []]));
    for (const f of shown) by.get(KIND_META[f.kind] ? f.kind : "functional").push(f);
    return KINDS.map((k) => ({ kind: k, rows: by.get(k) })).filter((s) => s.rows.length);
  }, [shown]);

  const counts = useMemo(() => {
    const c = { total: features.length, wants: 0, changed: 0, missing: 0 };
    for (const s of STATUSES) c[s] = 0;
    for (const k of KINDS) c[`kind_${k}`] = 0;
    for (const f of features) {
      if (c[f.status] != null) c[f.status]++;
      c[`kind_${KIND_META[f.kind] ? f.kind : "functional"}`]++;
      if (f.wants) c.wants++;
      if (f.delta_state === "changed") c.changed++;
      if (f.delta_state === "missing") c.missing++;
    }
    return c;
  }, [features]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="features-tab">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <ListChecks size={20} /> Requirements
        </h1>
        <div className="text-sm text-page-muted mt-1">
          everything asked of <b>{projectSlug}</b> and everything it demonstrably
          does — capabilities, qualities, constraints, design rules and explicit
          non-goals. Each requirement traces back either to the app that
          implements it or to the source document that asked for it, carries a
          check you can walk through, and shows the work and the evidence
          behind it.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Chip>{counts.total} requirement(s)</Chip>
        {KINDS.filter((k) => k !== "functional" && counts[`kind_${k}`] > 0).map((k) => (
          <Chip key={k}>{counts[`kind_${k}`]} {kindMeta(k).chip}</Chip>
        ))}
        {STATUSES.map((s) => counts[s] > 0 && (
          <Chip key={s} tone={statusMeta(s).tone}>{counts[s]} {statusMeta(s).label.toLowerCase()}</Chip>
        ))}
        {counts.wants > 0 && (
          <Chip tone="text-amber-700 dark:text-amber-400">{counts.wants} want a task</Chip>
        )}
        {cur ? (
          <label className="inline-flex items-center gap-1 text-xs text-page-muted ml-1">
            <History size={12} /> version
            <select data-testid="features-version" value={viewing ?? ""} onChange={(e) => setVersion(e.target.value ? Number(e.target.value) : null)}
              className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}{v.version === cur ? " (current)" : ""}{v.materials_version ? ` · materials v${v.materials_version}` : ""}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {viewing ? (
          <button type="button" data-testid="features-diff-toggle" onClick={() => toggleDiff(viewing)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
            {cmp?.by === "features" && cmp.to === viewing ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {cmp?.by === "features" && cmp.to === viewing ? "Hide changes" : `What changed in v${viewing}`}
          </button>
        ) : null}
        {sourcesWithRequirements.length >= 2 ? (
          <button type="button" data-testid="features-compare-sources" onClick={compareBySources}
            title="The requirements delta between two SOURCES versions — e.g. without and with a change request"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
            <GitCompareArrows size={11} />
            {cmp?.by === "materials" ? "Hide the comparison" : "Compare by sources version"}
          </button>
        ) : null}
        <div className="relative ml-auto">
          <button type="button" data-testid="features-summary" onClick={() => setSummaryMenu((v) => !v)}
            disabled={!!summaryBusy}
            title="The customer-facing summary of this project, generated from these requirements, the tests and the implementation — print it, download it, or save it to the project's Knowledge"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text disabled:opacity-50">
            {summaryBusy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Summary <ChevronDown size={11} />
          </button>
          {summaryMenu ? (
            <div data-testid="features-summary-menu" className="absolute right-0 top-full z-40 mt-1 w-72 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text text-sm"
              onMouseLeave={() => setSummaryMenu(false)}>
              <button type="button" data-testid="features-summary-page" onClick={openSummaryPage} className="w-full text-left px-3 py-2 flex items-center gap-2 bg-transparent border-0 cursor-pointer hover:bg-card-border/30">
                <Printer size={14} className="opacity-70" /> <span>Print-ready page <span className="text-page-muted">· Save as PDF</span></span>
              </button>
              <button type="button" data-testid="features-summary-md" onClick={downloadSummaryMd} className="w-full text-left px-3 py-2 flex items-center gap-2 bg-transparent border-0 cursor-pointer hover:bg-card-border/30">
                <Download size={14} className="opacity-70" /> Download as Markdown
              </button>
              <button type="button" data-testid="features-summary-archive" onClick={archiveSummary} className="w-full text-left px-3 py-2 flex items-center gap-2 bg-transparent border-0 cursor-pointer hover:bg-card-border/30">
                <BookMarked size={14} className="opacity-70" /> Save a copy to Knowledge
              </button>
              <div className="my-1 border-t border-card-border" />
              <button type="button" data-testid="features-summary-notes" onClick={openNotes} className="w-full text-left px-3 py-2 flex items-center gap-2 bg-transparent border-0 cursor-pointer hover:bg-card-border/30">
                <Pencil size={14} className="opacity-70" /> <span>Edit notes <span className="text-page-muted">· audience, decisions needed, caveats</span></span>
              </button>
            </div>
          ) : null}
        </div>
        <Link to={createPageUrl("status")} data-testid="features-status-link"
          title="Every test of the project with its latest verdict — the health of what was asked for"
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider no-underline text-page-muted hover:text-page-text hover:bg-card-border/30">
          <Activity size={12} /> Status page
        </Link>
        <button type="button" data-testid="features-review" onClick={review}
          disabled={busy === "review" || running.length > 0 || !analysis.allowed}
          data-analysis-state={analysis.state}
          data-build-id={heuristic?.request_id || undefined}
          title={!analysis.allowed ? (analysis.reason || "The build from the current sources is still running.")
            : data?.versions?.length
              ? "Review ONLY the sources added or changed since the last review, and re-check what the project implements — never a regeneration; overrides, tasks and edits are kept"
              : "Derive the requirements from the sources and check each against what the project already implements — optional; a build from the sources runs first"}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50 disabled:cursor-not-allowed">
          {busy === "review" || running.length ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {running.length ? "Analysing…" : !analysis.allowed ? (analysis.state === "preview" ? "Analyse — after the build preview" : `Analyse — after build #${heuristic?.request_id}`) : "Analyse this project"}
        </button>
      </div>

      {/* The running banner that used to live here is gone: the page-level
          ReviewStatusStrip says the same thing and four more states besides
          (queued, scheduled, failed-with-a-reason), from one shared fetch. Two
          banners for one review was the duplication that let a FAILED review
          show as nothing at all on this tab. */}
      {msg ? <div className="text-xs text-page-muted" data-testid="features-msg">{msg}</div> : null}
      {err ? <div className="text-sm text-red-600" data-testid="features-error">{err}</div> : null}
      {notesOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="summary-notes-modal" role="dialog" aria-modal="true" aria-label="Summary notes"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setNotesOpen(false); }}>
          <div className="w-full max-w-2xl max-h-[88vh] overflow-auto rounded-xl border border-card-border bg-card-bg p-5 flex flex-col gap-3 text-page-text">
            <div className="text-base font-semibold">Summary notes</div>
            <p className="text-xs text-page-muted m-0">Everything else in the summary is derived from the requirements, the tests and the implementation. These three parts are yours: they appear verbatim in every download.</p>
            <label className="text-xs font-medium">Who the summary is for
              <input data-testid="summary-notes-audience" className="mt-1 w-full rounded-md border border-page-divider bg-transparent px-2 py-1.5 text-sm" placeholder="e.g. Cool Solutions Group — eSPACE product and engineering leads"
                value={notes.audience} onChange={(e) => setNotes({ ...notes, audience: e.target.value })} />
            </label>
            <label className="text-xs font-medium">Decisions and inputs we need from the customer
              <textarea data-testid="summary-notes-decisions" rows={7} className="mt-1 w-full rounded-md border border-page-divider bg-transparent px-2 py-1.5 text-sm font-mono" placeholder={"1. …\n2. …"}
                value={notes.decisions} onChange={(e) => setNotes({ ...notes, decisions: e.target.value })} />
            </label>
            <label className="text-xs font-medium">Caveats
              <textarea data-testid="summary-notes-caveats" rows={3} className="mt-1 w-full rounded-md border border-page-divider bg-transparent px-2 py-1.5 text-sm" placeholder="e.g. development environment; synthetic data re-seeds on restart"
                value={notes.caveats} onChange={(e) => setNotes({ ...notes, caveats: e.target.value })} />
            </label>
            <div className="flex justify-end gap-2 mt-1">
              <button type="button" onClick={() => setNotesOpen(false)} className="text-sm px-3 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer">Cancel</button>
              <button type="button" data-testid="summary-notes-save" onClick={saveNotes} disabled={summaryBusy === "save"}
                className="text-sm px-3 py-1.5 rounded-md bg-nav-active text-white border-0 cursor-pointer disabled:opacity-50">
                {summaryBusy === "save" ? "Saving…" : "Save notes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {cmp?.to != null ? (
        <CompareRequirements featuresVersions={versions} compare={cmp} onChange={setCmp} onClose={() => setCmp(null)}
          onOpenFeature={(key) => { const f = features.find((x) => x.key === key); if (f) setOpen((o) => new Set([...o, f.slug])); document.querySelector(`[data-testid="feature-${key}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }); }}
          onOpenMaterial={onOpenMaterial} />
      ) : null}

      {features.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-page-muted">Status</span>
          <select data-testid="features-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer">
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
          </select>
          <span className="text-page-muted ml-1">Kind</span>
          <select data-testid="features-filter-kind" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer">
            <option value="all">All</option>
            <option value="functional">Functional</option>
            <option value="quality">Quality</option>
            <option value="constraint">Constraint</option>
            <option value="design">Design</option>
            <option value="non_goal">Out of scope</option>
            <option value="noise">Not a requirement</option>
          </select>
          <span className="text-page-muted ml-1">Origin</span>
          <select data-testid="features-filter-origin" value={originFilter} onChange={(e) => setOriginFilter(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer">
            <option value="all">All</option>
            <option value="material">From a source</option>
            <option value="as-built">As-built</option>
          </select>
          <label className="inline-flex items-center gap-1 text-page-muted cursor-pointer ml-1">
            <input type="checkbox" className="cursor-pointer" checked={wantsOnly} data-testid="features-filter-wants"
              onChange={(e) => setWantsOnly(e.target.checked)} />
            needs a task
          </label>
          <span className="text-page-muted ml-auto">{shown.length} shown</span>
        </div>
      )}

      {features.length > 0 && (!viewing || viewing === cur) && (
        <div className={"flex flex-wrap items-center gap-2 text-xs rounded-lg border px-3 py-2 " + (selected.size ? "border-nav-active/60" : "border-dashed border-page-divider")}
          style={selected.size ? { background: "var(--table-stripe)" } : undefined} data-testid="features-batch-bar">
          <label className="inline-flex items-center gap-1.5 text-page-muted cursor-pointer"
            title="Ticks every shown requirement that is not implemented yet (planned or in progress) — the ones a task can still deliver">
            <input type="checkbox" className="cursor-pointer" data-testid="features-select-all"
              checked={selectable.length > 0 && selectable.every((f) => selected.has(f.key))}
              onChange={(e) => setSelected(e.target.checked
                ? new Set([...selected, ...selectable.map((f) => f.key)])
                : new Set([...selected].filter((k) => !selectable.some((f) => f.key === k))))} />
            select all not implemented ({selectable.length})
          </label>
          <span className="font-medium text-page-text" data-testid="features-selected-count">{selected.size} selected</span>
          <span className="text-page-muted">→ one common task, worked</span>
          <select data-testid="features-batch-mode" value={batchMode} onChange={(e) => setBatchMode(e.target.value)} disabled={busy === "batch"}
            title={BATCH_MODES.find(([k]) => k === batchMode)?.[2] || ""}
            className="text-xs px-2 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
            {BATCH_MODES.map(([k, label, why]) => <option key={k} value={k} title={why}>{label}{k === "heuristic" ? " (default)" : ""}</option>)}
          </select>
          <span className="text-page-muted hidden md:inline max-w-[38ch] truncate" title={BATCH_MODES.find(([k]) => k === batchMode)?.[2] || ""}>
            — {BATCH_MODES.find(([k]) => k === batchMode)?.[2] || ""}
          </span>
          <button type="button" data-testid="features-batch-create" disabled={!selected.size || busy === "batch"} onClick={createBatch}
            className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed">
            {busy === "batch" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Create one task for <span data-testid="features-batch-count" className="tabular-nums">{selected.size}</span>
          </button>
          {selected.size ? (
            <button type="button" data-testid="features-batch-clear" onClick={() => setSelected(new Set())}
              className="text-xs text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Clear</button>
          ) : null}
        </div>
      )}

      {data == null && (
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}

      {data != null && features.length === 0 && !err && (
        <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2" data-testid="features-empty">
          <div className="font-medium">No analysis yet.</div>
          <div className="text-page-muted">
            {analysis.state === "preview" ? (
              <>The requirement sources are waiting for a <b>build preview</b> to be approved (Requirement sources tab). The build runs from the
              approved variant; when it is done, press <b>Analyse this project</b> to derive the requirements and see what is covered.</>
            ) : analysis.state === "building" ? (
              <>The project is being built directly from its requirement sources (build #{heuristic?.request_id}, {heuristic?.status}).
              When it is done, press <b>Analyse this project</b> to derive the requirements and see what is covered.</>
            ) : (
              <>The project was built directly from its requirement sources. Press <b>Analyse this project</b> to
              derive the requirements from the sources and from what the project already implements, and see what is
              covered and what is still a gap.</>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {sections.map((sec) => (
          <div key={sec.kind} className="space-y-1.5" data-testid={`features-section-${sec.kind}`}>
            {sections.length > 1 ? (
              <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-1">
                {kindMeta(sec.kind).section} <span className="font-normal">· {sec.rows.length}</span>
              </div>
            ) : null}
        {sec.rows.map((f) => {
          const isOpen = open.has(f.slug);
          const sm = statusMeta(f.status);
          const dm = DELTA_META[f.delta_state];
          const rows = (f.tasks || []).map((id) => tasks[id]).filter(Boolean);
          return (
            <div key={f.slug} data-testid={`feature-${f.key}`}
              className={"rounded-lg border " + (f.wants ? "border-amber-500/40" : "border-page-divider")}>
              <div className="flex items-start gap-2 px-3 py-2">
                {takesTasks(f.kind) && (!viewing || viewing === cur) ? (
                  <input type="checkbox" className="mt-1 cursor-pointer shrink-0" data-testid={`feature-select-${f.key}`}
                    checked={selected.has(f.key)} onChange={() => toggleSelect(f.key)} aria-label={`Select ${f.name} for a common task`} />
                ) : null}
                <button type="button" data-testid={`feature-toggle-${f.key}`} onClick={() => toggle(f.slug)}
                  className="mt-0.5 text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0 shrink-0">
                  {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" onClick={() => toggle(f.slug)}
                      className="text-left bg-transparent border-0 p-0 cursor-pointer text-sm font-medium text-page-text hover:text-nav-active">
                      {f.name}
                    </button>
                    {dm ? <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${dm.tone}`} title={dm.why}
                      data-testid={`feature-delta-${f.key}`}>{dm.label}</span> : null}
                    {kindMeta(f.kind).chip ? (
                      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${kindMeta(f.kind).tone}`}
                        data-testid={`feature-kind-${f.key}`}>{kindMeta(f.kind).chip}</span>
                    ) : null}
                    <span className="shrink-0 text-[11px] text-page-muted inline-flex items-center gap-1">
                      {f.origin === "material" ? <ClipboardList size={11} /> : <LayoutGrid size={11} />}
                      {f.origin === "material" ? "from a source" : "as-built"}
                    </span>
                    {f.area_label ? (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-page-divider text-page-muted inline-flex items-center gap-1"
                        data-testid={`feature-area-${f.key}`}
                        title={`Area: ${f.area_label} — its tasks are claimed and built together with this area's other tasks`}>
                        <Tag size={10} /> {f.area_label}
                      </span>
                    ) : null}
                    {f.origin === "material" && f.verified === false ? (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400"
                        title="The quote could not be found in the cited material">unconfirmed quote</span>
                    ) : null}
                    {(f.citations || []).length ? (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-page-divider text-page-muted"
                        data-testid={`feature-citations-${f.key}`}
                        title="Other passages of the sources linked to this row — highlighted on Requirement sources">
                        +{f.citations.length} passage{f.citations.length === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-page-muted mt-0.5 line-clamp-2">{f.description}</div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {takesTasks(f.kind) ? (
                    <span className={`text-xs font-medium ${sm.tone}`} data-testid={`feature-status-${f.key}`}>{sm.label}</span>
                  ) : null}
                  {f.overridden ? <span className="text-[10px] text-page-muted" title={`Derived: ${statusMeta(f.derived).label}`}>by hand</span> : null}
                  {rows.length ? <span className="text-[11px] text-page-muted" title={`${rows.length} task(s)`}>{rows.length} task(s)</span> : null}
                  <TestsBadge tests={testsByFeature[f.key]} testid={`feature-tests-badge-head-${f.key}`} />
                  <button type="button" data-testid={`feature-delete-${f.key}`} disabled={busy === f.slug}
                    onClick={() => removeRequirement(f)}
                    title="Delete this requirement (its tasks stay; a later review may re-add it)"
                    className="text-page-muted hover:text-red-700 dark:hover:text-red-400 bg-transparent border-0 cursor-pointer p-0.5 disabled:opacity-50">
                    <Trash2 size={12} />
                  </button>
                  {f.wants ? (
                    // "+ <kind>" files the task the requirement CALLS FOR — an app
                    // change, a pipeline change, or a build on another cortex
                    // surface (model / agent / workflow / guide / report). The
                    // select lets a person pick a different kind before filing.
                    <span className="inline-flex items-stretch rounded-md border border-page-divider overflow-hidden" title={f.wants.why}>
                      <button type="button" data-testid={`feature-add-task-${f.key}`} disabled={busy === f.slug}
                        onClick={() => addTask(f, f.wants)}
                        className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-transparent border-0 cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
                        {busy === f.slug ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                        {TASK_KIND_LABELS[kindPick[f.slug] || f.wants.kind] || "change"}
                      </button>
                      {/* a chevron that IS the select: the native control sits on top, invisible, so the menu is the browser's */}
                      <span className="relative inline-flex items-center px-1 border-l border-page-divider text-page-muted" title="Choose another task kind">
                        <ChevronDown size={11} aria-hidden="true" />
                        <select data-testid={`feature-task-kind-${f.key}`} aria-label="Task kind"
                          value={kindPick[f.slug] || f.wants.kind} disabled={busy === f.slug}
                          onChange={(e) => setKindPick((m) => ({ ...m, [f.slug]: e.target.value }))}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-default">
                          {TASK_KINDS.map((k) => <option key={k} value={k}>{TASK_KIND_LABELS[k]}</option>)}
                        </select>
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>

              {isOpen ? (
                <div className="px-3 pb-3 pl-8 space-y-3 text-sm" data-testid={`feature-detail-${f.key}`}>
                  {f.description ? <div className="text-page-text">{f.description}</div> : null}

                  <div>
                    <Label>Origin</Label>
                    {f.origin === "material" && f.material_id ? (
                      <div className="space-y-1">
                        <button type="button" data-testid={`feature-origin-${f.key}`}
                          onClick={() => setOrigin(originOf(f))}
                          title="Show this passage in the material"
                          className="inline-flex items-center gap-1 text-xs text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline">
                          <ClipboardList size={12} /> {f.material_name || `material #${f.material_id}`}
                          {f.position != null ? <span className="text-page-muted"> · at character {f.position}</span> : null}
                        </button>
                        {f.quote ? (
                          // The quote is the other half of the same link. A
                          // reader whose eye is on the sentence should not have
                          // to travel back up to the file name to see it in
                          // place — a verified quote opens the passage too.
                          <blockquote
                            {...(f.verified === false ? {} : {
                              role: "button", tabIndex: 0,
                              "data-testid": `feature-origin-quote-${f.key}`,
                              onClick: () => setOrigin(originOf(f)),
                              onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOrigin(originOf(f)); } },
                              title: "Show this passage in the material",
                            })}
                            className={"border-l-2 border-page-divider pl-2 text-xs text-page-muted italic"
                              + (f.verified === false ? "" : " cursor-pointer hover:border-nav-active hover:text-page-text")}>
                            "{f.quote}"
                            {f.verified === false ? (
                              <span className="not-italic text-amber-700 dark:text-amber-400"> — ⚠ not found in that material</span>
                            ) : null}
                          </blockquote>
                        ) : null}
                        {f.app_view ? (
                          // Asked for AND already built: the document is the
                          // origin, the app is the proof it exists — both links
                          // belong on the row.
                          <Link to={createPageUrl(`apps/${encodeURIComponent(f.app_view)}`)}
                            data-testid={`feature-implemented-by-${f.key}`}
                            className="inline-flex items-center gap-1 text-xs text-page-muted no-underline hover:underline hover:text-nav-active">
                            <LayoutGrid size={12} /> implemented by {f.app_title || f.app_view}
                          </Link>
                        ) : null}
                        {f.built_ref ? (
                          <div className="text-xs text-page-muted" data-testid={`feature-built-ref-${f.key}`}>
                            implemented in the pipeline/platform: <span className="font-mono">{f.built_ref}</span>
                          </div>
                        ) : null}
                      </div>
                    ) : f.app_view ? (
                      // An as-built origin links the APP THAT IMPLEMENTS IT, so
                      // the reader can open the thing and see the feature for
                      // themselves. That is the check a specification page
                      // could never offer.
                      <Link to={createPageUrl(`apps/${encodeURIComponent(f.app_view)}`)}
                        data-testid={`feature-origin-app-${f.key}`}
                        className="inline-flex items-center gap-1 text-xs text-nav-active no-underline hover:underline">
                        <LayoutGrid size={12} /> {f.app_title || f.app_view}
                      </Link>
                    ) : f.built_ref ? (
                      // As-built in the GOVERNANCE CONFIGURATION or the fork
                      // code (a rule, a source, a transformation, an endpoint,
                      // a model, a workflow…) rather than a screen: name the
                      // object, so the reader can find it on its own page.
                      <div className="text-xs text-page-muted" data-testid={`feature-built-ref-${f.key}`}>
                        As-built — implemented in the pipeline/platform: <span className="font-mono">{f.built_ref}</span>
                      </div>
                    ) : (
                      <div className="text-xs text-page-muted">As-built — no material asked for it; the project implements it.</div>
                    )}
                  </div>

                  {(f.use_cases || []).length ? (
                    <div>
                      <Label>Use cases — walk one to check it works</Label>
                      {f.use_cases.map((uc, i) => (
                        <pre key={i} data-testid={`feature-usecase-${f.key}-${i}`}
                          className="text-xs whitespace-pre-wrap rounded-md px-2.5 py-2 overflow-x-auto mb-1"
                          style={{ background: "var(--table-stripe)" }}>{uc}</pre>
                      ))}
                    </div>
                  ) : null}

                  {takesTasks(f.kind) ? (
                    <FeatureTests projectSlug={projectSlug} feature={f}
                      tests={testsByFeature[f.key] || []}
                      generating={testJobs.find((j) => j.feature_key === f.key) || null}
                      onChanged={onTestsChanged} readOnly={!!(viewing && cur && viewing !== cur)} />
                  ) : null}

                  {!takesTasks(f.kind) ? (
                    <div className="text-xs text-page-muted" data-testid={`feature-no-tasks-${f.key}`}>
                      {(f.kind || "functional") === "noise"
                        ? <>Not a requirement — this row records text of the sources that asks nothing of the project (headings, boilerplate, background), so that no passage goes unread. It takes no tasks and no status.</>
                        : <>Explicitly out of scope — this requirement records what the project does NOT do, so it takes no tasks and no status.</>}
                    </div>
                  ) : (
                  <div>
                    <Label>Tasks</Label>
                    {rows.length ? (
                      <ul className="space-y-1">
                        {rows.map((t) => (
                          <li key={t.id} className="text-xs" data-testid={`feature-task-${t.id}`}>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-page-muted">{t.kind}</span>
                              <span className="text-page-text">{t.title}</span>
                              {t.request_id ? (
                                <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/board`}
                                  className={`px-1.5 py-0.5 rounded no-underline hover:underline ${boardTone(t.board_status)}`}>
                                  #{t.request_id} · {boardLabel(t.board_status)} <ExternalLink size={9} className="inline" />
                                </a>
                              ) : <span className="text-page-muted">(not filed)</span>}
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${t.evidence ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-card-border/40 text-page-muted"}`}>
                                {t.evidence ? `${t.evidence} evidence` : "no evidence"}
                              </span>
                            </div>
                            <EvidenceList taskId={t.id} rows={evidence[t.id]} projectSlug={projectSlug}
                              onChanged={async () => { setEvidence((m) => { const n = { ...m }; delete n[t.id]; return n; }); await loadEvidence(t.id); await load(version); }} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs text-page-muted">
                        No task yet.{f.wants ? " Use the button above to create one." : ""}
                      </div>
                    )}
                  </div>
                  )}

                  {takesTasks(f.kind) ? (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Label inline>Status</Label>
                    <select data-testid={`feature-set-status-${f.key}`} value={f.override || ""} disabled={busy === f.slug}
                      onChange={(e) => setOverride(f.slug, e.target.value || null)}
                      className="text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text cursor-pointer disabled:opacity-50">
                      <option value="">Auto ({statusMeta(f.derived).label})</option>
                      {STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                    </select>
                    {busy === f.slug ? <Loader2 size={12} className="animate-spin text-page-muted" /> : null}
                    {f.overridden ? (
                      <span className="text-[11px] text-page-muted inline-flex items-center gap-1">
                        <Pencil size={10} /> set by hand — kept through every review
                      </span>
                    ) : null}
                    {f.materials_version ? (
                      <span className="ml-auto text-[11px] text-page-muted">from sources v{f.materials_version}</span>
                    ) : null}
                  </div>
                  ) : null}

                  {f.delta_state === "missing" ? (
                    <div className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      No source supports this requirement any more. It is kept for review rather
                      than deleted — decide whether it was dropped or renamed.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
          </div>
        ))}
      </div>

      {origin ? (
        <OriginModal
          material={{ id: origin.material_id, name: origin.material_name }}
          quote={origin.quote}
          position={origin.position}
          // The version the REVIEW read, not the live text: the offset was
          // computed against that snapshot, and a material edited since would
          // otherwise move the highlight off the sentence it belongs to.
          version={origin.materials_version}
          onClose={() => setOrigin(null)}
          onOpenInMaterials={(id) => onOpenMaterial?.(id)}
        />
      ) : null}
    </div>
  );
}

// The origin fields the modal needs, lifted off a feature row. Two places open
// it today (the file-name link and the quote), and anything that renders an
// origin later must open the SAME passage — a second hand-rolled object is how
// one of them quietly starts passing the live text's offset instead of the
// reviewed version's.
//
// Nothing else in the product renders an origin: the features DIFF panel lists
// names only (its `shape` on the backend drops quote/position deliberately),
// and a Timeline event is about a whole material, not a passage, so both are
// correctly left as they are.
function originOf(f) {
  if (!f || f.origin !== "material" || !f.material_id) return null;
  return {
    material_id: f.material_id,
    material_name: f.material_name || null,
    quote: f.quote || "",
    position: f.position ?? null,
    materials_version: f.materials_version ?? null,
  };
}

function Chip({ tone = "text-page-muted", children }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${tone}`}
      style={{ background: "var(--card-border, rgba(127,127,127,.18))" }}>{children}</span>
  );
}
function Label({ children, inline }) {
  return (
    <div className={(inline ? "inline " : "") + "text-[11px] font-semibold uppercase tracking-wide text-page-muted mb-1"}>
      {children}
    </div>
  );
}

// The evidence on ONE task — and the form that adds it. Evidence is what turns
// "someone closed the card" into "here is how you check it yourself", so the
// kinds are deliberately concrete: a commit, a screenshot, how it was tested,
// and steps a human can follow.
const EV_KINDS = [
  ["steps", "Steps to verify"],
  ["test_notes", "How it was tested"],
  ["commit", "Commit"],
  ["screenshot", "Screenshot"],
  ["link", "Link"],
];

function EvidenceList({ taskId, rows, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("steps");
  const [body, setBody] = useState("");
  const [ref, setRef] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await fetchData(`docs/tasks/${encodeURIComponent(taskId)}/evidence`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, title: title || null, body: body || null, ref: ref || null }),
      });
      setBody(""); setRef(""); setTitle(""); setAdding(false);
      await onChanged?.();
    } catch (e) { setErr(String(e?.message || "Could not save the evidence.")); }
    finally { setBusy(false); }
  };

  const needsBody = kind === "steps" || kind === "test_notes";
  return (
    <div className="pl-2 mt-1 space-y-1">
      {(rows || []).map((e) => (
        <div key={e.id} className="text-[11px] text-page-muted" data-testid={`evidence-${e.id}`}>
          <span className="font-semibold">{(EV_KINDS.find((k) => k[0] === e.kind) || [, e.kind])[1]}</span>
          {e.title ? <span> — {e.title}</span> : null}
          {e.ref ? <span className="font-mono"> {e.ref}</span> : null}
          {e.body ? <pre className="whitespace-pre-wrap mt-0.5 rounded px-2 py-1" style={{ background: "var(--table-stripe)" }}>{e.body}</pre> : null}
        </div>
      ))}
      {adding ? (
        <div className="space-y-1 rounded-md border border-page-divider p-2" data-testid={`evidence-form-${taskId}`}>
          <div className="flex items-center gap-1.5">
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid={`evidence-kind-${taskId}`}
              className="text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
              {EV_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="title (optional)"
              className="flex-1 text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text" />
          </div>
          {!needsBody ? (
            <input value={ref} onChange={(e) => setRef(e.target.value)} data-testid={`evidence-ref-${taskId}`}
              placeholder={kind === "commit" ? "commit hash" : kind === "screenshot" ? "image URL or key" : "URL"}
              className="w-full text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
          ) : (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid={`evidence-body-${taskId}`}
              placeholder={kind === "steps" ? "1. Open …\n2. Click …\n3. You should see …" : "What was run, and what it showed."}
              className="w-full text-[11px] px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
          )}
          {err ? <div className="text-[11px] text-red-600">{err}</div> : null}
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} disabled={busy} data-testid={`evidence-save-${taskId}`}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
            </button>
            <button type="button" onClick={() => setAdding(false)} className="text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid={`evidence-add-${taskId}`}
          className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0">
          <Plus size={10} /> add evidence
        </button>
      )}
    </div>
  );
}
